import { useEffect, useRef, useState } from "react";
import ProductSpinner from "./ProductSpinner";

/**
 * A single, real product that visually travels from the hero anchor
 * to the product-showcase anchor as the user scrolls.
 *
 * Renders a fixed overlay above the page; position/size are computed
 * from two real anchor DOM elements so it always lands exactly where
 * it should on any viewport. The figurine itself is the ProductSpinner,
 * which auto-rotates while in the hero and pauses while travelling /
 * once it has settled into the showcase slot.
 */

export const HERO_ANCHOR_ID = "journey-hero-anchor";
export const SHOWCASE_ANCHOR_ID = "journey-showcase-anchor";

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, mn: number, mx: number) => Math.min(mx, Math.max(mn, v));

const ProductJourney = () => {
  const [box, setBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
    progress: number; // 0 = in hero, 1 = in showcase slot
    opacity: number;
    visible: boolean;
  } | null>(null);

  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      const heroEl = document.getElementById(HERO_ANCHOR_ID);
      const showEl = document.getElementById(SHOWCASE_ANCHOR_ID);
      if (!heroEl) {
        setBox((b) => (b ? { ...b, visible: false } : null));
        return;
      }

      const heroR = heroEl.getBoundingClientRect();

      if (!showEl) {
        setBox({
          left: heroR.left,
          top: heroR.top,
          width: heroR.width,
          height: heroR.height,
          progress: 0,
          opacity: 1,
          visible: true,
        });
        return;
      }

      const showR = showEl.getBoundingClientRect();
      const vh = window.innerHeight;

      const heroAbsTop = heroR.top + window.scrollY;
      const showAbsTop = showR.top + window.scrollY;
      const scrollY = window.scrollY;

      const startScroll = heroAbsTop - vh * 0.45;
      const endScroll = showAbsTop - vh * 0.55;

      let p = (scrollY - startScroll) / Math.max(1, endScroll - startScroll);
      p = clamp(p, 0, 1);
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;

      const left = lerp(heroR.left, showR.left, e);
      const top = lerp(heroR.top, showR.top, e);
      const width = lerp(heroR.width, showR.width, e);
      const height = lerp(heroR.height, showR.height, e);

      const pastShowcase = showR.bottom < -50;
      const beforeHero = heroR.bottom > vh + 200 && scrollY < startScroll - 50;

      setBox({
        left,
        top,
        width,
        height,
        progress: e,
        opacity: pastShowcase ? 0 : 1,
        visible: !pastShowcase && !beforeHero,
      });
    };

    const tick = () => {
      update();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const onResize = () => update();
    window.addEventListener("resize", onResize);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  if (!box || !box.visible) return null;

  const inHero = box.progress < 0.06;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        pointerEvents: inHero ? "auto" : "none",
        zIndex: 30,
        opacity: box.opacity,
        transition: "opacity 0.4s ease",
        willChange: "transform, left, top, width, height",
      }}
    >
      <ProductSpinner interactive={inHero} paused={false} loopMs={5200} />
    </div>
  );
};

export default ProductJourney;
